import React, { useState } from "react";
import styled from "styled-components";
import FintorroLogo from "../assets/fintorro-logo.png";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

/* ---------------------- Styled Components ------------------------- */

const DashboardWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #111315;
  color: #e2e2e2;
  padding: 32px;
  font-family: "Nunito Sans", sans-serif;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

/* Left side */
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

/* Right side */
const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.img`
  height: 60px;
  border-radius: 8px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #ffffff;
`;

const ProductSelector = styled.select`
  background: #1f1f1f;
  border: 1px solid #333;
  padding: 10px 14px;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  background: ${(props) => (props.active ? "#FF7C24" : "#1f1f1f")};
  color: ${(props) => (props.active ? "#000" : "#fff")};
  transition: 0.2s;

  &:hover {
    background: #ff7c24;
    color: #000;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  background: #1a1d1f;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 0 0 1px #242729;
`;

const FullWidthCard = styled(Card)`
  grid-column: 1 / -1;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 16px;
  color: #fff;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
`;

const StatBox = styled.div`
  background: #242729;
  padding: 18px;
  border-radius: 10px;
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 14px;
  opacity: 0.7;
  margin-bottom: 6px;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #ff7c24;
`;

/* ---------------------- Dummy Data Generator ------------------------- */

function generateDashboardData(days) {
  const data = [];
  for (let i = 0; i < days; i++) {
    const dailyUsers =
      125000 +
      Math.floor(Math.sin(i / 2) * 8000 + Math.random() * 3000 + 20000);

    data.push({
      date: `Day ${i + 1}`,
      dailyActiveUsers: Math.max(121000, dailyUsers), // always above 120k
      activeConnections: Math.floor(1 + Math.random() * 30),
      dailyUploadsGB: parseFloat((Math.random() * 8 + 2).toFixed(2)),
      dailyUsageGB: parseFloat((Math.random() * 12 + 3).toFixed(2)),
      storageTotalGB: parseFloat((200 + i * 0.3).toFixed(2)),
    });
  }
  return data;
}

/* ----------------------------- Main Component ----------------------------- */

export default function AnalyticsDashboard() {
  const [range, setRange] = useState(30);
  const [product, setProduct] = useState("Educational Institutions");

  // calendar default = November 20 2025
  const [selectedDate, setSelectedDate] = useState(new Date("2025-11-20"));

  const data = generateDashboardData(range);

  return (
    <DashboardWrapper>

      {/* Top Header */}
      <HeaderRow>

        {/* Left side */}
        <PageHeader>
          <Logo src={FintorroLogo} alt="FinTorro Logo" />
          <PageTitle>Fintorro Firebase Dashboard</PageTitle>
        </PageHeader>

        {/* Right side */}
        <RightControls>

          <ProductSelector
            value={product}
            onChange={(e) => setProduct(e.target.value)}
          >
            <option>Educational Institutions</option>
            <option>App Users</option>
            <option>Ask The Chart AI Users</option>
            <option>Free Users</option>
            <option>Premium Users</option>
          </ProductSelector>

          <DatePicker
            onChange={setSelectedDate}
            value={selectedDate}
            clearIcon={null}
            calendarIcon={null}
            format="y-MM-dd"
          />

        </RightControls>
      </HeaderRow>

      {/* Range Filters */}
      <FilterRow>
        {[1, 7, 30, 60, 90].map((d) => (
          <FilterButton key={d} active={range === d} onClick={() => setRange(d)}>
            {d} days
          </FilterButton>
        ))}
      </FilterRow>

      {/* Summary Section */}
      <FullWidthCard>
        <CardTitle>Overview — {product}</CardTitle>

        <StatGrid>
          <StatBox>
            <StatLabel>Educational Institutions</StatLabel>
            <StatNumber>153,217</StatNumber>
          </StatBox>

          <StatBox>
            <StatLabel>Web & App Users</StatLabel>
            <StatNumber>150,983</StatNumber>
          </StatBox>

          <StatBox>
            <StatLabel>Ask The Chart AI Users</StatLabel>
            <StatNumber>18,213</StatNumber>
          </StatBox>

          <StatBox>
            <StatLabel>Free Users</StatLabel>
            <StatNumber>131,587</StatNumber>
          </StatBox>

          <StatBox>
            <StatLabel>Premium Users</StatLabel>
            <StatNumber>19,347</StatNumber>
          </StatBox>
        </StatGrid>
      </FullWidthCard>

      {/* Charts */}
      <Grid>

        <Card>
          <CardTitle>Daily Active Users</CardTitle>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="dailyActiveUsers"
                stroke="#4dabf7"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardTitle>Active Connections Per Second</CardTitle>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="activeConnections"
                stroke="#22c55e"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardTitle>Daily Uploads (GB)</CardTitle>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="dailyUploadsGB"
                stroke="#FF7C24"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardTitle>Daily Usage (GB)</CardTitle>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="dailyUsageGB"
                stroke="#8b5cf6"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <FullWidthCard>
          <CardTitle>Total Storage (GB)</CardTitle>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="storageTotalGB"
                stroke="#f43f5e"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </FullWidthCard>

      </Grid>
    </DashboardWrapper>
  );
}